import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';
import { logOut, openSideNav, registerRoutes } from '../../../func';
import UploadModal from './uploadModal';
import Axios from 'axios';
import * as xlsx from 'xlsx';
import ResolveManufacturerNamesModal from './resolveManufacturerNamesModal';
import UnmatchedCreditsModal from './unmatchedCreditsModal';
import moment from 'moment';
import { useDispatch } from 'react-redux';

const McKesson = () => {
  const authData = useMemo(
    () => JSON.parse(localStorage.getItem('auth_data')),
    []
  );
  const { path } = useRouteMatch();
  const dispatch = useDispatch();

  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
  const [
    isOpenResolveManufacturerNamesModal,
    setIsOpenResolveManufacturerNamesModal,
  ] = useState(false);
  const [isOpenUnmatchedCreditsModal, setIsOpenUnmatchedCreditsModal] =
    useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [workbook, setWorkbook] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [manufacturerList, setManufacturerList] = useState(null);
  const [aliases, setAliases] = useState(null);
  const [manufacturerNames, setManufacturerNames] = useState(null);
  const [unknownManufacturerNames, setUnknownManufacturerNames] =
    useState(null);
  const [unmatchedCredits, setUnmatchedCredits] = useState(null);
  const [debitMemoList, setDebitMemoList] = useState([]);
  const [hydratedDebitMemoList, setHydratedDebitMemoList] = useState(null);

  const networkCalls = useMemo(
    () =>
      registerRoutes(
        {
          downloadTemplate: {
            func: () => {
              setIsDownloadingTemplate(true);
              Axios.get('/api/v1/creditmemo/read/creditmemotemplate', {
                params: authData,
                responseType: 'blob',
              })
                .then(({ data }) => {
                  const blob = new Blob([data]);
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.hidden = true;
                  link.href = url;
                  link.setAttribute('download', 'credit_memo_template.xlsx');
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                })
                .catch(logOut)
                .finally(() => setIsDownloadingTemplate(false));
            },
            type: 'r',
          },
          getManufacturerList: {
            func: () => {
              Axios.get('/api/v2/party/manufacturer/read/all', {
                params: authData,
              })
                .then(({ data }) => setManufacturerList(data))
                .catch(logOut);
            },
            type: 'r',
          },
          getUnmatchedCredits: {
            func: debitMemoList => {
              Axios.post('/api/v2/debitmemo/read/unmatchedmckessoncredits', {
                ...authData,
                debitMemoList,
              })
                .then(({ data }) => {
                  setDebitMemoList(data);
                  setUnmatchedCredits(
                    data.filter(({ debitMemoId }) => !Boolean(debitMemoId))
                  );
                })
                .catch(logOut);
            },
            type: 'r',
          },
          getPharmacyList: {
            func: (debitMemoList, sheet) => {
              Axios.post('/api/v2/debitmemo/read/mckessonpharmacylist', {
                ...authData,
                debitMemoIds: debitMemoList.map(
                  ({ debitMemoId }) => debitMemoId
                ),
              })
                .then(({ data }) => {
                  const arr = [];

                  sheet.forEach(row => {
                    const debitMemoId =
                      debitMemoList.find(
                        ({ reportedDebitMemoNumber }) =>
                          reportedDebitMemoNumber ===
                          row[`Reported Debit Memo Number`]
                      )?.debitMemoId || 0;

                    const pharmacyInfo = data.find(
                      ({ debitMemo: { id } }) =>
                        parseInt(id) === parseInt(debitMemoId)
                    );

                    arr.push({
                      ...row,
                      ...pharmacyInfo,
                    });
                  });

                  setHydratedDebitMemoList(arr);
                })
                .catch(logOut);
            },
            type: 'r',
          },
        },
        path
      ),
    [path, authData]
  );

  useEffect(() => {
    networkCalls.getManufacturerList();
  }, [networkCalls]);

  useEffect(() => {
    if (!manufacturerList) return;
    const aliases = manufacturerList
      .filter(mfr => mfr.manufacturerAliases)
      .flatMap(({ manufacturerAliases }) => manufacturerAliases)
      .map(({ alias }) => alias);

    setAliases(aliases);
    setManufacturerNames(manufacturerList.map(({ name }) => name));
  }, [manufacturerList]);

  useEffect(() => {
    if (!workbook) return;

    const sheet = xlsx.utils.sheet_to_json(
      workbook.Sheets['Credit Report Upload']
    );

    setSheet(sheet);

    const reportedManufacturerNames = sheet
      .map(row => row['Reported Manufacturer Name'])
      .filter((name, index, thisArray) => thisArray.indexOf(name) === index)
      .sort();

    const unknowns = reportedManufacturerNames.filter(
      reportedName =>
        !manufacturerNames?.find(
          name => name.toLocaleLowerCase() === reportedName.toLocaleLowerCase()
        ) &&
        !aliases?.find(
          alias =>
            alias.toLocaleLowerCase() === reportedName.toLocaleLowerCase()
        )
    );

    setUnknownManufacturerNames(unknowns);
  }, [workbook, manufacturerNames, aliases]);

  useEffect(() => {
    if (unknownManufacturerNames && unknownManufacturerNames.length)
      return setIsOpenResolveManufacturerNamesModal(true);
    if (sheet)
      networkCalls.getUnmatchedCredits(
        sheet.map(row => ({
          manufacturerName: row['Reported Manufacturer Name'],
          debitMemoNumber: row['Reported Debit Memo Number'],
        }))
      );
  }, [unknownManufacturerNames, sheet, networkCalls]);

  useEffect(() => {
    if (debitMemoList?.length && unmatchedCredits && !unmatchedCredits?.length)
      networkCalls.getPharmacyList(debitMemoList, sheet);
  }, [unmatchedCredits, sheet, debitMemoList, networkCalls]);

  const updateUnmatchedCredit = useCallback(
    (reportedDebitMemoNumber, debitMemoId) => {
      const index = debitMemoList.findIndex(
        row => row.reportedDebitMemoNumber === reportedDebitMemoNumber
      );
      debitMemoList[index].debitMemoId = debitMemoId;
      setDebitMemoList([...debitMemoList]);

      const unmatchedIndex = unmatchedCredits.findIndex(
        row => row.reportedDebitMemoNumber === reportedDebitMemoNumber
      );
      unmatchedCredits.splice(unmatchedIndex, 1);
      setUnmatchedCredits([...unmatchedCredits]);
    },
    [debitMemoList, unmatchedCredits, setDebitMemoList, setUnmatchedCredits]
  );

  const downloadExcelFile = event => {
    event.preventDefault();
    try {
      const vendorInfo = [];
      const customerInfo = [];

      hydratedDebitMemoList.forEach(dm => {
        const { debitMemo, pharmacy, wholesalerAccount } = dm;

        if (!debitMemo || !pharmacy || !wholesalerAccount)
          throw new Error('You have an unmatched credit. Try again.');

        const batchDate = `ORX${moment(debitMemo.dateCreated).format('MMDD')}`;
        const mckessonFee = dm['Full Credit Amount'] * 0.05;
        const processFee = pharmacy.isVa
          ? 0.0
          : dm['Full Credit Amount'] - mckessonFee;
        const netCredit = dm['Full Credit Amount'] - mckessonFee - processFee;

        //Excel dates are counted as days since Dec 30, 1899
        const wholesalerProcessDate = moment('1899-12-30')
          .add(dm[`Wholesaler Process Date`], 'days')
          .format('MM/DD/YYYY');

        vendorInfo.push({
          'Batch Date': batchDate,
          'AP Number': dm[`Wholesaler Document Number`],
          'Manufacturer Name': dm[`Reported Manufacturer Name`],
          'Credit Memo Number': dm[`Credit Memo Number`],
          'Credit Issue Date': wholesalerProcessDate,
          'Gross Total': dm[`Full Credit Amount`],
          'Packing Slip': dm[`Reported Debit Memo Number`],
          'Process Fee': processFee.toFixed(2),
          'MCK Fee': mckessonFee.toFixed(2),
        });

        customerInfo.push({
          'Batch Date': batchDate,
          'Customer Number': wholesalerAccount,
          PharmacyName: pharmacy.name.toLocaleUpperCase(),
          'Net Credit': netCredit.toFixed(2),
          'DEA Number': pharmacy.deaNumber,
          'Corrected Debit Memo Number': debitMemo.debitMemoNumber,
          'Credit Memo Number': dm['Credit Memo Number'],
        });
      });

      const wb = xlsx.utils.book_new();
      const viSheet = xlsx.utils.json_to_sheet(vendorInfo);
      const ciSheet = xlsx.utils.json_to_sheet(customerInfo);
      const viSheetWidths = {};

      Object.keys(viSheet).forEach(key => {
        const [col, row] = key.match(/[^\d]+|\d+/g);
        if (col.search('!') === -1) {
          const len = viSheet[key].v?.toString().length;
          if (!viSheetWidths[col]) viSheetWidths[col] = [];

          viSheetWidths[col].push(len);

          if (parseInt(row) > 1) {
            switch (col) {
              case 'E':
                viSheet[key].t = 'd';
                break;
              case 'F':
                viSheet[key].t = 'n';
                viSheet[key].z = '0.00';
                break;
              case 'H':
                viSheet[key].t = 'n';
                viSheet[key].z = '0.00';
                break;
              case 'I':
                viSheet[key].t = 'n';
                viSheet[key].z = '0.00';
                break;
              default:
                break;
            }
          }
        }
      });
      const viSheetWidthsFinal = [];

      Object.keys(viSheetWidths).forEach(key => {
        viSheetWidthsFinal.push({
          wch: Math.max(...viSheetWidths[key]),
        });
      });

      viSheet['!cols'] = viSheetWidthsFinal;

      const ciSheetWidths = {};

      Object.keys(ciSheet).forEach(key => {
        const [col, row] = key.match(/[^\d]+|\d+/g);
        if (col.search('!') === -1) {
          const len = ciSheet[key].v?.toString().length;
          if (!ciSheetWidths[col]) ciSheetWidths[col] = [];

          ciSheetWidths[col].push(len);

          if (row > 1 && col === 'D') {
            ciSheet[key].t = 'n';
            ciSheet[key].z = '0.00';
          }
        }
      });
      const ciSheetWidthsFinal = [];

      Object.keys(ciSheetWidths).forEach(key => {
        ciSheetWidthsFinal.push({
          wch: Math.max(...ciSheetWidths[key]),
        });
      });

      ciSheet['!cols'] = ciSheetWidthsFinal;

      xlsx.utils.book_append_sheet(wb, viSheet, 'Vendor Information');
      xlsx.utils.book_append_sheet(wb, ciSheet, 'Customer Information');
      xlsx.writeFile(wb, 'mckesson_credit_report.xlsx');
    } catch (err) {
      dispatch({
        type: 'GLOBAL_TOAST',
        payload: {
          msg: err.toString(),
          class: 'red white-text',
        },
      });
    }
  };

  return (
    <div className="main">
      <div className="row">
        <div style={{ display: 'flex' }}>
          <Link to="/" onClick={openSideNav} style={{ marginRight: '12px' }}>
            <i className="material-icons black-text">menu</i>
          </Link>
          <Link to="/">Home</Link>
          <i className="material-icons">chevron_right</i>
          <Link to="/accounting">Accounting</Link>
          <i className="material-icons">chevron_right</i>
          <span className="grey-text">McKesson B-Sheistery</span>
        </div>
      </div>
      <div className="row">
        <div className="col s12 m3 offset-m6">
          <a
            href="/"
            onClick={e => {
              e.preventDefault();
              setIsOpenUploadModal(true);
            }}
            className="btn-small blue white-text waves-effect waves light col s12"
          >
            Upload McKesson File
          </a>
        </div>
        <div className="col s12 m3">
          <button
            onClick={networkCalls.downloadTemplate}
            className="btn-small white blue-text waves-effect waves-dark col s12"
            disabled={isDownloadingTemplate}
          >
            Download Excel Template
          </button>
        </div>
      </div>
      <div className="row">
        {unmatchedCredits && unmatchedCredits.length ? (
          <div className="col s12 m3 offset-m9">
            <a
              href="/"
              className="btn-small blue white-text waves-effect waves-light col s12"
              onClick={e => {
                e.preventDefault();
                setIsOpenUnmatchedCreditsModal(true);
              }}
            >
              View Unmatched Credits
            </a>
          </div>
        ) : null}
      </div>
      <div className="row">
        {hydratedDebitMemoList &&
        hydratedDebitMemoList.length &&
        unmatchedCredits &&
        !unmatchedCredits.length ? (
          <div className="col s12 m3 offset-m9">
            <a
              href="/"
              className="btn-small blue white-text waves-effect waves-light col s12"
              onClick={downloadExcelFile}
            >
              Download Excel File
            </a>
          </div>
        ) : null}
      </div>
      <ResolveManufacturerNamesModal
        isOpen={isOpenResolveManufacturerNamesModal}
        setIsOpen={setIsOpenResolveManufacturerNamesModal}
        unknownManufacturerNames={unknownManufacturerNames}
        setUnknownManufacturerNames={setUnknownManufacturerNames}
        manufacturerList={manufacturerList}
      />
      <UploadModal
        isOpen={isOpenUploadModal}
        setIsOpen={setIsOpenUploadModal}
        setWorkbook={setWorkbook}
      />
      <UnmatchedCreditsModal
        isOpen={isOpenUnmatchedCreditsModal}
        setIsOpen={setIsOpenUnmatchedCreditsModal}
        unmatchedCredits={unmatchedCredits}
        sheet={sheet}
        updateUnmatchedCredit={updateUnmatchedCredit}
      />
    </div>
  );
};

export default McKesson;
